import React from "react";
import { styled } from "styled-components";
import { colors } from "../../styles/theme";

const phases = [
  {
    title: "Discovery & Alignment",
    description:
      "We start every engagement by listening. Collaborative workshops and stakeholder interviews help us unpack business goals, user needs, and any hidden constraints before a single line of code is written.",
    takeaway: "Framing canvas, measurable objectives, prioritised backlog",
  },
  {
    title: "Solution Architecture",
    description:
      "Our architects map the optimal technical approach, de-risk critical integrations, and ensure security, scalability, and performance are designed in from day one.",
    takeaway: "Solution blueprint, delivery roadmap, risk register",
  },
  {
    title: "Design & Build",
    description:
      "Cross-functional squads blend UX, engineering, and quality assurance to move ideas into production-ready software through rapid, iterative delivery.",
    takeaway: "Interactive prototypes, fully tested increments, transparent reporting",
  },
  {
    title: "Launch & Enablement",
    description:
      "Coordinated go-live support, change management, and capability enablement make sure releases land smoothly for your teams and end-users.",
    takeaway: "Cut-over plan, success metrics, playbooks and documentation",
  },
  {
    title: "Continuous Evolution",
    description:
      "We stay close after launch with data-informed enhancements, proactive monitoring, and embedded teams that keep your platforms modern and resilient.",
    takeaway: "Runbooks, optimisation backlog, quarterly innovation cycles",
  },
];

const ProcessSection = () => {
  return (
    <SectionContainer id="process">
      <Title
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                gap: 10,
                alignItems: "center",
                fontWeight: "bolder",
                color: colors.primary,
              }}
              data-aos="fade"
            >
              <hr
                style={{
                  width: 2,
                  height: 30,
                  border: "none",
                  borderRadius: 5,
                  backgroundColor: colors.primary,
                  margin: 0,
                }}
              />How We Deliver</Title>
      <SectionSubhead data-aos="fade-up" data-aos-delay="100">
        A transparent, repeatable delivery framework that keeps every project on
        track and stakeholders confident.
      </SectionSubhead>
      <Timeline>
        {phases.map((phase, index) => (
          <PhaseCard key={phase.title} data-aos="fade-up" data-aos-delay={index * 80}>
            <PhaseIndex>{String(index + 1).padStart(2, "0")}</PhaseIndex>
            <PhaseTitle>{phase.title}</PhaseTitle>
            <PhaseDescription>{phase.description}</PhaseDescription>
            <Divider />
            <TakeawayLabel>Key takeaways</TakeawayLabel>
            <Takeaway>{phase.takeaway}</Takeaway>
          </PhaseCard>
        ))}
      </Timeline>
    </SectionContainer>
  );
};

const SectionContainer = styled.section`
  padding: 6% 8%;
  display: flex;
  flex-direction: column;
  gap: 40px;

  @media (max-width: 768px) {
    padding: 15% 5%;
  }
`;

const Title = styled.h2`
  color: white;
  margin: 0 !important;
  padding: 0;
  font-size: 24px;
`;

const SectionSubhead = styled.p`
  font-size: 16px;
  color: #686868;
  margin: 0;
  padding: 0;
  line-height: 1.6;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const Timeline = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  justify-content: center;
`;

const PhaseCard = styled.article`
  background-color: #151515;
  border-radius: 24px;
  padding: 28px;
  min-height: 260px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  gap: 14px;
  position: relative;
  overflow: hidden;
  transition: transform 0.4s ease, box-shadow 0.4s ease;
  flex: 1 1 280px;
  max-width: 320px;
  width: 100%;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 28px 60px rgba(0, 0, 0, 0.45);
  }
`;

const PhaseIndex = styled.span`
  font-size: 14px;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
`;

const PhaseTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  color: white;
`;

const PhaseDescription = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.75);
  font-size: 15px;
  line-height: 1.6;
`;

const Divider = styled.span`
  height: 1px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.35), rgba(255, 255, 255, 0));
`;

const TakeawayLabel = styled.span`
  font-size: 12px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
`;

const Takeaway = styled.p`
  margin: 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
`;

export default ProcessSection;
